import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Grid3X3, 
  List, 
  Eye, 
  Edit, 
  Star, 
  Lock, 
  Unlock, 
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Calendar,
  DollarSign,
  Palette,
  User,
  Volume2,
  Play,
  Pause,
  Plus,
  BookOpen,
  X
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import '../styles/AudioArtworksAdmin.css';

const AudioArtworksAdmin = () => {
  const navigate = useNavigate();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [audioRefs, setAudioRefs] = useState({});

  const itemsPerPage = 20;

  // Fetch artworks with audio
  const fetchArtworks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Try Edge Function first, fallback to direct query
      try {
        const { data, error } = await supabase.functions.invoke('get_artworks_with_audio', {
          body: {
            page: currentPage,
            limit: itemsPerPage,
            search: searchTerm,
            sortBy,
            sortOrder,
            isPublic: false // Admin view - show all
          }
        });

        if (error) throw error;

        if (data.success) {
          setArtworks(data.data);
          setTotalPages(data.pagination.totalPages);
          setTotalCount(data.pagination.total);
          return;
        }
      } catch (funcError) {
        console.warn('Edge function failed, using direct query:', funcError);
      }

      // Fallback: Direct Supabase query
      const offset = (currentPage - 1) * itemsPerPage;
      
      // First, get artwork IDs that have audio clips
      const { data: audioArtworkIds, error: audioError } = await supabase
        .from('media_elements')
        .select('vc_id')
        .eq('type', 'Audio')
        .eq('status', 'Public');

      if (audioError) {
        throw audioError;
      }

      if (!audioArtworkIds || audioArtworkIds.length === 0) {
        setArtworks([]);
        setTotalPages(0);
        setTotalCount(0);
        return;
      }

      const artworkIds = audioArtworkIds.map(item => item.vc_id);
      
      // Build query for artworks using vcs table
      let query = supabase
        .from('vcs')
        .select(`
          vc_id, title, description, cover_image, price, width, height, depth, access_control, created_at, updated_at, uuid,
          users!inner(uuid, nickname, first_name, last_name, handle)
        `)
        .in('vc_id', artworkIds)
        .eq('access_control', 'Public')
        .ilike('cover_image', '%res.cloudinary.com/dh9jfogh1%');

      // Add search filter
      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,users.nickname.ilike.%${searchTerm}%,users.handle.ilike.%${searchTerm}%`);
      }

      // Add sorting
      if (sortBy === 'price') {
        query = query.order('price', { ascending: sortOrder === 'asc' });
      } else if (sortBy === 'title') {
        query = query.order('title', { ascending: sortOrder === 'asc' });
      } else if (sortBy === 'collection_count') {
        query = query.order('collection_count', { ascending: sortOrder === 'asc' });
      } else {
        query = query.order('created_at', { ascending: sortOrder === 'asc' });
      }

      // Add pagination
      query = query.range(offset, offset + itemsPerPage - 1);

      const { data: artworks, error: artworksError, count } = await query;

      if (artworksError) {
        throw artworksError;
      }

      // Get total count
      const { count: totalCount } = await supabase
        .from('vcs')
        .select('*', { count: 'exact', head: true })
        .in('vc_id', artworkIds)
        .eq('access_control', 'Public')
        .ilike('cover_image', '%res.cloudinary.com/dh9jfogh1%');

      // Fetch audio clips for all artworks
      const { data: allAudioClips, error: audioClipsError } = await supabase
        .from('media_elements')
        .select('*')
        .eq('type', 'Audio')
        .eq('status', 'Public')
        .in('vc_id', artworks?.map(a => a.vc_id) || []);

      if (audioClipsError) {
        console.error('Audio clips error:', audioClipsError);
      }

      // Group audio clips by artwork ID
      const audioClipsByArtwork = (allAudioClips || []).reduce((acc, clip) => {
        if (!acc[clip.vc_id]) {
          acc[clip.vc_id] = [];
        }
        acc[clip.vc_id].push(clip);
        return acc;
      }, {});

      // Process the data to include audio information
      const processedArtworks = artworks?.map(artwork => {
        const audioClips = audioClipsByArtwork[artwork.vc_id] || [];
        const user = artwork.users?.[0] || {}; // Get first user from array
        return {
          ...artwork,
          id: artwork.vc_id, // Map vc_id to id for consistency
          audio_clips: audioClips,
          audio_count: audioClips.length,
          has_audio: audioClips.length > 0,
          artist_name: user.nickname || 
                      `${user.first_name || ''} ${user.last_name || ''}`.trim() || 
                      user.handle || 'Unknown Artist',
          collection_count: 0 // Default value, can be fetched separately if needed
        };
      }) || [];

      setArtworks(processedArtworks);
      setTotalPages(Math.ceil((totalCount || 0) / itemsPerPage));
      setTotalCount(totalCount || 0);

    } catch (err) {
      console.error('Error fetching artworks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    fetchArtworks();
  }, [fetchArtworks]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchArtworks();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Audio controls
  const toggleAudio = (artworkId, audioIndex) => {
    const audioKey = `${artworkId}-${audioIndex}`;
    const audio = audioRefs[audioKey];
    
    if (!audio) return;

    if (playingAudio === audioKey) {
      audio.pause();
      setPlayingAudio(null);
    } else {
      // Pause any currently playing audio
      Object.values(audioRefs).forEach(a => a.pause());
      
      audio.play();
      setPlayingAudio(audioKey);
    }
  };

  const handleAudioRef = (artworkId, audioIndex, ref) => {
    if (ref) {
      setAudioRefs(prev => ({
        ...prev,
        [`${artworkId}-${audioIndex}`]: ref
      }));
    }
  };

  const handleAudioEnded = () => {
    setPlayingAudio(null);
  };

  // Lightbox functions
  const openLightbox = (artwork) => {
    setSelectedArtwork(artwork);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedArtwork(null);
    setPlayingAudio(null);
  };

  // Utility functions
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDimensions = (width, height, depth) => {
    let dimensions = `${width}"W × ${height}"H`;
    if (depth && depth > 0) {
      dimensions += ` × ${depth}"D`;
    }
    return dimensions;
  };

  // Pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-btn ${i === currentPage ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn"
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-btn"
        >
          Next
        </button>
      </div>
    );
  };

  if (loading && artworks.length === 0) {
    return (
      <div className="audio-artworks-admin">
        <div className="loading-container">
          <RefreshCw className="loading-spinner" />
          <p>Loading artworks with audio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="audio-artworks-admin">
        <div className="error-container">
          <h2>Error Loading Artworks</h2>
          <p>{error}</p>
          <button onClick={fetchArtworks} className="retry-btn">
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="audio-artworks-admin">
      <div className="admin-header">
        <div className="header-content">
          <div className="header-title">
            <Volume2 size={24} />
            <h1>Artworks with Audio</h1>
            <span className="count-badge">{totalCount} artworks</span>
          </div>
          <p className="header-subtitle">
            Manage and review artworks that include audio explanations and music
          </p>
        </div>
      </div>

      <div className="admin-controls">
        <div className="search-controls">
          <div className="search-input-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search artworks, artists, or descriptions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="view-controls">
          <div className="sort-controls">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="created_at">Date Created</option>
              <option value="title">Title</option>
              <option value="price">Price</option>
              <option value="collection_count">Collections</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="sort-order-btn"
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>

          <div className="view-toggle">
            <button
              onClick={() => setViewMode('grid')}
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              title="Grid View"
            >
              <Grid3X3 size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              title="List View"
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="artworks-container">
        {artworks.length === 0 ? (
          <div className="empty-state">
            <Volume2 size={48} />
            <h3>No Artworks with Audio Found</h3>
            <p>No artworks with audio clips match your current search criteria.</p>
          </div>
        ) : (
          <div className={`artworks-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
            {artworks.map((artwork) => (
              <div key={artwork.id} className="artwork-card-new">
                <div className="artwork-image-container">
                  <img
                    src={artwork.cover_image || '/images/placeholder-artwork.jpg'}
                    alt={artwork.title}
                    className="artwork-image"
                    onClick={() => openLightbox(artwork)}
                  />
                  <div className="audio-indicator">
                    <Volume2 size={16} />
                    <span>{artwork.audio_count}</span>
                  </div>
                </div>

                <div className="artwork-info">
                  <h3 className="artwork-title">{artwork.title}</h3>
                  <p className="artist-name">{artwork.artist_name}</p>
                  
                  {artwork.width && artwork.height && (
                    <p className="artwork-dimensions">
                      {formatDimensions(artwork.width, artwork.height, artwork.depth)}
                    </p>
                  )}
                  
                  {artwork.price && (
                    <p className="artwork-price">{formatPrice(artwork.price)}</p>
                  )}

                  <div className="artwork-meta">
                    <span className="collections-count">{artwork.collection_count} collections</span>
                    <span className="audio-count">{artwork.audio_count} audio clips</span>
                  </div>

                  {/* Audio Preview */}
                  <div className="audio-preview">
                    {artwork.audio_clips?.slice(0, 2).map((audio, index) => (
                      <div key={index} className="audio-item">
                        <button
                          onClick={() => toggleAudio(artwork.id, index)}
                          className="audio-play-btn"
                        >
                          {playingAudio === `${artwork.id}-${index}` ? (
                            <Pause size={14} />
                          ) : (
                            <Play size={14} />
                          )}
                        </button>
                        <span className="audio-title">
                          {audio.title || `Audio ${index + 1}`}
                        </span>
                        <audio
                          ref={(ref) => handleAudioRef(artwork.id, index, ref)}
                          src={audio.source}
                          onEnded={handleAudioEnded}
                          preload="none"
                        />
                      </div>
                    ))}
                    {artwork.audio_count > 2 && (
                      <span className="more-audio">+{artwork.audio_count - 2} more</span>
                    )}
                  </div>

                  <div className="artwork-actions-row">
                    <button
                      onClick={() => openLightbox(artwork)}
                      className="action-btn view-btn"
                      title="View artwork details"
                    >
                      <Eye size={14} />
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 && renderPagination()}

      {/* Lightbox */}
      {lightboxOpen && selectedArtwork && (
        <div className="artwork-modal" onClick={closeLightbox}>
          <div className="artwork-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="artwork-modal-header">
              <h3>{selectedArtwork.title}</h3>
              <button onClick={closeLightbox}>×</button>
            </div>
            <div className="artwork-modal-body">
              <div className="artwork-modal-image">
                <img 
                  src={selectedArtwork.cover_image || '/images/placeholder-artwork.jpg'} 
                  alt={selectedArtwork.title}
                />
              </div>
              <div className="artwork-modal-details">
                <p><strong>Artist:</strong> {selectedArtwork.artist_name}</p>
                {selectedArtwork.width && selectedArtwork.height && (
                  <p><strong>Dimensions:</strong> {formatDimensions(selectedArtwork.width, selectedArtwork.height, selectedArtwork.depth)}</p>
                )}
                {selectedArtwork.price && <p><strong>Price:</strong> {formatPrice(selectedArtwork.price)}</p>}
                {selectedArtwork.medium && <p><strong>Medium:</strong> {selectedArtwork.medium}</p>}
                {selectedArtwork.description && <p><strong>Description:</strong> {selectedArtwork.description}</p>}
                <p><strong>Created:</strong> {formatDate(selectedArtwork.created_at)}</p>
                <p><strong>Collections:</strong> {selectedArtwork.collection_count} collections</p>
                <p><strong>Audio Clips:</strong> {selectedArtwork.audio_count} clips</p>
                
                {/* Audio Section */}
                <div className="audio-section">
                  <h4>Audio Clips</h4>
                  <div className="audio-clips-list">
                    {selectedArtwork.audio_clips?.map((audio, index) => (
                      <div key={index} className="audio-clip-item">
                        <button
                          onClick={() => toggleAudio(selectedArtwork.id, index)}
                          className="audio-play-btn-large"
                        >
                          {playingAudio === `${selectedArtwork.id}-${index}` ? (
                            <Pause size={20} />
                          ) : (
                            <Play size={20} />
                          )}
                        </button>
                        <div className="audio-info">
                          <span className="audio-title">
                            {audio.title || `Audio Clip ${index + 1}`}
                          </span>
                          <span className="audio-date">
                            {formatDate(audio.created_at)}
                          </span>
                        </div>
                        <audio
                          ref={(ref) => handleAudioRef(selectedArtwork.id, index, ref)}
                          src={audio.source}
                          onEnded={handleAudioEnded}
                          preload="none"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="artwork-engagement-details">
                  <h4>Actions</h4>
                  <div className="collection-actions">
                    <button 
                      className="view-details-btn"
                      onClick={() => {
                        navigate(`/artwork/${selectedArtwork.vc_id}`);
                        closeLightbox();
                      }}
                      title="View public artwork page"
                    >
                      <Eye size={16} />
                      View Public Page
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioArtworksAdmin;
